import React, { useEffect, useMemo } from 'react';
import { Timeline } from './index';
import { NonIdealState } from '@blueprintjs/core';
import { NO_DATA_MESSAGE } from 'widgets/chartUtils';
import { sum } from 'd3-array';
import { useWidgetControlsContext } from 'views/common/WidgetControls';

/**
 * ChartWrapper for the Timeline Graph.
 *
 * @param widgetConfiguration
 * @param dimensions
 * @param data
 * @returns {*}
 * @constructor
 */
export function TimelineGraphWrapper({
	dimensions,
	data,
	noDataMessage = NO_DATA_MESSAGE,
	widgetConfiguration = { stacked: false, cropEmptyExtents: false, xAxisLabel: "", yAxisLabel: "" },
	getColorScale,
}) {
	const stacked = !!widgetConfiguration?.stacked;
	const {dispatch, state} = useWidgetControlsContext();
	const hideLabels = state?.hideLabels;
	const convertedData = useMemo(() => {
		// the data is null or in the wrong shape and we exit
		if (!data || !Array.isArray(data.bins))
			return;

		// empty data is "valid"
		if (data.bins?.length === 0)
			return [];

		// If there is no data, return nothing so that the
		// no data display will show
		let totalCount = 0;
		for (const bin in data.bins) {
			for (const binData in data.bins[bin]['binData']) {
				totalCount += stacked
					? sum(Object.values(data.bins[bin]['binData'][binData].counts))
					: data.bins[bin]['binData'][binData].count;
			}
		}
		if (totalCount === 0) return;

		if (stacked) {
			getColorScale.domain(data.keys);
		}
		return data;
	}, [data, stacked, getColorScale]);

	//has to be in a 'useEffect' as opposed to the useMemo above bc of the way react is.
	useEffect(() => {
		// the data is null or in the wrong shape and we exit
		if (!data || !Array.isArray(data.bins) || data.bins?.length === 0) {
			dispatch && dispatch({ type: "setLabelsToggleBtnStatus", payload: false });
			return;
		}
		dispatch && dispatch({ type: "setLabelsToggleBtnStatus", payload: true });

	}, [data]);

	//If the state is not ideal (the data is null or empty) render the nonIdealState message
	return convertedData ? (
		<Timeline
			dimensions={dimensions}
			data={convertedData}
			stacked={stacked}
			getColorScale={getColorScale}
			stackOrder={widgetConfiguration?.stackOrder}
			cropEmptyExtents={widgetConfiguration?.cropEmptyExtents}
			units={widgetConfiguration?.units}
			hideLabels={hideLabels}
			xAxisLabel={widgetConfiguration?.xAxisLabel}
			yAxisLabel={widgetConfiguration?.yAxisLabel}
		/>
	) : (
		<NonIdealState title="No Data Found" icon="graph-remove" description={noDataMessage} />
	);
}
